<template>
  <div class="support-page">
    <nav class="navbar">
      <router-link to="/" class="nav-logo">
        <AppLogo class="logo-img" />
      </router-link>
      <router-link v-if="!isAuthenticated" to="/login" class="btn-login">Login</router-link>
      <router-link v-else to="/dashboard" class="btn-login">Dashboard</router-link>
    </nav>

    <div class="support-container">
      <div class="support-content">
        <h1>Support</h1>
        <p class="support-intro">
          Need help? Reach us at
          <a href="mailto:support@resymaid.com" class="support-email">support@resymaid.com</a>
        </p>

        <section class="bug-report-section">
          <h2>Bug report / Feedback</h2>
          <p class="section-desc">Describe the issue or send feedback. We'll get back to you as soon as we can.</p>

          <div v-if="submitted" class="status-content success">
            <p>Thank you. Your message has been sent and we'll get back to you soon.</p>
          </div>

          <form v-else @submit.prevent="submitBugReport" class="bug-report-form">
            <div class="form-group">
              <label for="support-email">Your email (optional)</label>
              <input
                id="support-email"
                type="email"
                v-model="form.email"
                placeholder="you@example.com"
              />
            </div>
            <div class="form-group">
              <label for="support-subject">Subject (optional)</label>
              <input
                id="support-subject"
                type="text"
                v-model="form.subject"
                placeholder="e.g. Bug when generating resume"
                maxlength="200"
              />
            </div>
            <div class="form-group">
              <label for="support-message">Message <span class="required">*</span></label>
              <textarea
                id="support-message"
                v-model="form.message"
                placeholder="Describe the issue or your feedback..."
                rows="5"
                required
                maxlength="5000"
              />
              <span class="char-count">{{ form.message.length }} / 5000</span>
            </div>
            <p v-if="formError" class="error-message">{{ formError }}</p>
            <button type="submit" class="btn-primary" :disabled="loading">
              {{ loading ? 'Sending...' : 'Send' }}
            </button>
          </form>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';
import { formatApiErrorMessage } from '@/axios';
import { mapGetters } from 'vuex';
import AppLogo from '@/components/AppLogo.vue';

export default {
  name: 'Support',
  components: { AppLogo },
  data() {
    return {
      form: {
        email: '',
        subject: '',
        message: '',
      },
      loading: false,
      formError: '',
      submitted: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'user']),
  },
  mounted() {
    if (this.user?.email) {
      this.form.email = this.user.email || '';
    }
  },
  methods: {
    async submitBugReport() {
      this.formError = '';
      if (!this.form.message?.trim()) {
        this.formError = 'Message is required.';
        return;
      }
      this.loading = true;
      try {
        await axios.post('/api/support/bug-report', {
          email: this.form.email?.trim() || undefined,
          subject: this.form.subject?.trim() || undefined,
          message: this.form.message.trim(),
          source: 'web',
        });
        this.submitted = true;
        this.form.message = '';
        this.form.subject = '';
      } catch (err) {
        this.formError = formatApiErrorMessage(err, 'Failed to send. Please email support@resymaid.com directly.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.support-page {
  min-height: 100vh;
  background: var(--app-bg);
  color: var(--app-text);
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--app-border);
}

.nav-logo {
  display: flex;
  align-items: center;
}

.logo-img {
  height: 64px;
  width: auto;
}

.btn-login {
  padding: 8px 16px;
  background: color-mix(in srgb, var(--app-primary) 20%, transparent);
  color: var(--app-primary);
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
}

.btn-login:hover {
  background: color-mix(in srgb, var(--app-primary) 30%, transparent);
}

.support-container {
  max-width: 560px;
  margin: 0 auto;
  padding: 48px 24px;
}

.support-content h1 {
  font-size: 28px;
  margin-bottom: 12px;
}

.support-intro {
  font-size: 16px;
  color: var(--app-text-secondary);
  margin-bottom: 40px;
}

.support-email {
  color: var(--app-primary);
  text-decoration: none;
  font-weight: 500;
}

.support-email:hover {
  text-decoration: underline;
}

.bug-report-section {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  padding: 24px;
}

.bug-report-section h2 {
  font-size: 18px;
  margin-bottom: 8px;
}

.section-desc {
  font-size: 14px;
  color: var(--app-text-secondary);
  margin-bottom: 20px;
}

.bug-report-form .form-group {
  margin-bottom: 16px;
}

.bug-report-form label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
  color: var(--app-text);
}

.bug-report-form .required {
  color: var(--app-error);
}

.bug-report-form input,
.bug-report-form textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--app-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--app-text);
  font-size: 14px;
  box-sizing: border-box;
}

.bug-report-form textarea {
  resize: vertical;
  min-height: 120px;
}

.bug-report-form input::placeholder,
.bug-report-form textarea::placeholder {
  color: var(--text-muted);
}

.char-count {
  display: block;
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 4px;
}

.error-message {
  color: var(--app-error);
  font-size: 14px;
  margin-bottom: 12px;
}

.btn-primary {
  padding: 10px 20px;
  background: var(--app-primary);
  color: var(--app-primary-text);
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
}

.btn-primary:hover:not(:disabled) {
  background: var(--app-primary-hover);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.status-content.success {
  padding: 16px;
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 8px;
  color: #86efac;
}
</style>
